import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, CheckCircle2, Download, FileArchive, Loader2, Lock, Save } from "lucide-react";
import { apiRequest, invalidateGeneratedAdListings } from "@/lib/queryClient";
import { AdImage } from "@/components/ad-image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { CtVariationImage } from "@shared/schema";
import { sizeDownloadExclusion } from "@shared/iteration-size-downloads";
import { useIterationSizeDownloads } from "@/lib/iteration-size-downloads";
import { IterationSizesActions } from "./iteration-sizes-actions";

export type VariationResultImage = CtVariationImage & {
  isSaved?: boolean;
  mediaType?: string | null;
  videoUrl?: string | null;
  setId?: string;
  variationSetId?: string;
};

export type VariationSetWithImages = {
  id: string;
  sourceCardId?: string;
  sourceName?: string;
  sourceAdId?: string;
  sourceImageUrl?: string;
  sourceVideoUrl?: string;
  sourceVideoPreviewUrl?: string;
  sourceStatus?: string;
  mediaType?: string | null;
  status: string;
  aspectRatio: string | null;
  error: string | null;
  keptLockedAt: string | Date | null;
  images: VariationResultImage[];
};

type FamilyDisplayCreative = {
  generatedAdId: string;
  name: string | null;
  archived: boolean;
  missing: boolean;
};
type CreativeFamily = {
  inspection?: {
    sourceAd?: FamilyDisplayCreative | null;
    immediateParent?: FamilyDisplayCreative | null;
    root?: FamilyDisplayCreative | null;
    contributingSources?: Array<{ source?: FamilyDisplayCreative | null; relation?: string }>;
    descendants?: Array<FamilyDisplayCreative | null>;
  };
  node?: { generationDepth?: number | null; generationBatchId?: string | null; iterationDirection?: string | null };
  launches?: Array<{ platform: string; accountId: string; campaignId: string; adsetId?: string | null; adId: string; creativeId: string; launchId: string }>;
  limitations?: { rootUnknown?: boolean; immediateParentUnknown?: boolean; ancestryTruncated?: boolean; descendantsTruncated?: boolean; cycleDetected?: boolean; maxDepth?: number };
};

function CreativeFamilyInspection({ brandId, generatedAdId, label }: { brandId?: string; generatedAdId?: string; label: string }) {
  const [open, setOpen] = useState(false);
  const family = useQuery<CreativeFamily>({
    queryKey: [`/api/brands/${brandId}/creative-families/${generatedAdId}`],
    enabled: open && !!brandId && !!generatedAdId,
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/brands/${brandId}/creative-families/${generatedAdId}`);
      const json = await response.json();
      if (!response.ok) throw new Error(json?.message || "Could not load lineage");
      return json;
    },
  });
  if (!generatedAdId) return <span className="text-[10px] text-muted-foreground">Lineage unavailable: this result has no saved creative ID yet.</span>;
  const creative = (item?: FamilyDisplayCreative | null) => item
    ? <>{item.name || "Unnamed creative"}{item.archived ? " (archived)" : ""}{item.missing ? " (no longer available)" : ""}</>
    : "Unknown";
  const inspection = family.data?.inspection;
  const limits = family.data?.limitations;
  return <div className="mt-2 border-t pt-2 text-[10px]" data-testid={`creative-family-${generatedAdId}`}>
    <button type="button" className="font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" onClick={() => setOpen(value => !value)} aria-expanded={open}>
      {open ? "Hide" : "Inspect"} lineage for {label}
    </button>
    {open && <div className="mt-2 rounded-md bg-muted/50 p-2 text-muted-foreground">
      {family.isLoading ? "Loading lineage…" : family.isError ? "Lineage is currently unavailable. Try again later." : family.data && <div className="space-y-1">
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
          <dt>Source ad</dt><dd className="font-medium text-foreground">{creative(inspection?.sourceAd)}</dd>
          <dt>Immediate parent</dt><dd>{limits?.immediateParentUnknown ? "Unknown" : creative(inspection?.immediateParent)}</dd>
          <dt>Root</dt><dd>{limits?.rootUnknown ? "Unknown" : creative(inspection?.root)}</dd>
          <dt>Depth</dt><dd>{family.data.node?.generationDepth ?? "Unknown"}</dd>
          <dt>Batch</dt><dd>{family.data.node?.generationBatchId ?? "Unknown"}</dd>
          <dt>Direction</dt><dd>{family.data.node?.iterationDirection ?? "Unknown"}</dd>
          <dt>Contributing sources</dt><dd>{inspection?.contributingSources?.length ? inspection.contributingSources.map(({ source, relation }) => `${source?.name || "Unknown"}${source?.archived ? " (archived)" : ""}${relation ? ` (${relation.replaceAll("_", " ")})` : ""}`).join(", ") : "Unknown"}</dd>
          <dt>Descendants</dt><dd>{inspection?.descendants?.length ? inspection.descendants.map(item => `${item?.name || "Unknown"}${item?.archived ? " (archived)" : ""}`).join(", ") : "None known"}</dd>
        </dl>
        {(limits?.ancestryTruncated || limits?.descendantsTruncated || limits?.cycleDetected) && <p role="status">Inspection incomplete: {[limits.ancestryTruncated && `ancestry exceeds ${limits.maxDepth} levels`, limits.descendantsTruncated && `descendants exceed ${limits.maxDepth} levels`, limits.cycleDetected && "a lineage cycle was detected"].filter(Boolean).join("; ")}.</p>}
        <div><span className="font-medium">Linked Meta launches:</span> {family.data.launches?.length ? <ul className="mt-1 space-y-0.5">{family.data.launches.map(launch => <li key={`${launch.platform}-${launch.accountId}-${launch.adId}-${launch.creativeId}`}>{launch.platform} account {launch.accountId} · campaign {launch.campaignId} · ad set {launch.adsetId || "Unknown"} · ad {launch.adId} · creative {launch.creativeId} · launch {launch.launchId}</li>)}</ul> : " None known"}</div>
      </div>}
    </div>}
  </div>;
}

/** The result controls shared by Creative Tinder and Creative Iterations. */
export function VariationSetResults<T extends VariationSetWithImages>({
  sets, onSetsChange, showSafeZone = false, safeZoneOverlay, saveOnly = false, brandId, multiSizeDownloadsEnabled = false,
}: {
  sets: T[];
  onSetsChange: (sets: T[]) => void;
  showSafeZone?: boolean;
  safeZoneOverlay?: ReactNode;
  /** Iterations use per-image save/unsave and download only; Tinder keeps its keep flow. */
  saveOnly?: boolean;
  /** Enables read-only lineage inspection for Creative Iterations. */
  brandId?: string;
  /** Effective master + user-grant + brand flag; only Iterations passes it. */
  multiSizeDownloadsEnabled?: boolean;
}) {
  const { toast } = useToast();
  const [selections, setSelections] = useState<Record<string, Set<string>>>({});
  const touched = useRef(new Set<string>());
  const [locking, setLocking] = useState<string | null>(null);
  const [zipping, setZipping] = useState<string | null>(null);
  const [savingImageId, setSavingImageId] = useState<string | null>(null);
  const [savedImageStates, setSavedImageStates] = useState<Record<string, boolean>>({});
  const sizeDownloadsEnabled = saveOnly && !!brandId && multiSizeDownloadsEnabled;
  const sizeDownloads = useIterationSizeDownloads(sizeDownloadsEnabled, brandId || "disabled");

  useEffect(() => {
    setSelections(prev => {
      const next = { ...prev }; let changed = false;
      for (const set of sets) {
        if (touched.current.has(set.id)) continue;
        const ready = set.images.filter(i => i.status === "rendered" && i.imageUrl);
        const ids = set.keptLockedAt ? ready.filter(i => (i as any).kept).map(i => i.id) : ready.map(i => i.id);
        if (!next[set.id] || next[set.id].size !== ids.length || ids.some(id => !next[set.id].has(id))) {
          next[set.id] = new Set(ids); changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sets]);

  const change = (id: string, fn: (s: Set<string>) => Set<string>) => {
    touched.current.add(id);
    setSelections(prev => ({ ...prev, [id]: fn(new Set(prev[id] ?? [])) }));
  };
  const downloadOne = (url: string, name: string) => {
    const a = document.createElement("a"); a.href = url; a.download = `${name}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const downloadVideo = (url: string, name: string) => {
    const a = document.createElement("a"); a.href = url; a.download = `${name}.mp4`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const lock = async (set: VariationSetWithImages) => {
    const ids = [...(selections[set.id] ?? [])];
    if (!ids.length) return toast({ title: "Nothing selected", description: "Pick at least one ad to keep.", variant: "destructive" });
    setLocking(set.id);
    try {
      const r = await apiRequest("PATCH", `/api/creative-tinder/variation-sets/${set.id}/keep`, { imageIds: ids });
      const json = await r.json();
      if (!r.ok) throw new Error(json?.message || `Failed to save (${r.status})`);
      // The keep endpoint returns the canonical set rows but not optional
      // Creative Iterations source labels. Preserve that display metadata.
      const saved = { ...set, ...json.set } as T;
      onSetsChange(sets.map(s => s.id === set.id ? saved : s));
      change(set.id, () => new Set(saved.images.filter((i: any) => i.kept && i.status === "rendered" && i.imageUrl).map(i => i.id)));
      toast({ title: "Selection saved", description: `${ids.length} ad${ids.length === 1 ? "" : "s"} locked in.` });
    } catch (e: any) { toast({ title: "Couldn't save selection", description: e?.message || "Unknown error", variant: "destructive" }); }
    finally { setLocking(null); }
  };
  const zip = async (set: VariationSetWithImages) => {
    setZipping(set.id);
    try {
      const r = await apiRequest("GET", `/api/creative-tinder/variation-sets/${set.id}/download`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || `Download failed (${r.status})`);
      const u = URL.createObjectURL(await r.blob()); const a = document.createElement("a");
      a.href = u; a.download = `creative-set-${set.id.slice(0, 8)}.zip`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch (e: any) { toast({ title: "Couldn't download", description: e?.message || "Unknown error", variant: "destructive" }); }
    finally { setZipping(null); }
  };
  const saveToMyAds = async (img: VariationResultImage) => {
    if (!img.generatedAdId) return false;
    const isSaved = savedImageStates[img.id] ?? img.isSaved === true;
    if (saveOnly && isSaved) return true;
    const nextSaved = !isSaved;
    setSavingImageId(img.id);
    try {
      const setId = img.setId || img.variationSetId;
      const endpoint = saveOnly && setId
        ? `/api/creative-tinder/variation-sets/${setId}/results/${img.id}/save`
        : `/api/generated-ads/${img.generatedAdId}/${nextSaved ? "save" : "unsave"}`;
      await apiRequest("POST", endpoint);
      invalidateGeneratedAdListings();
      setSavedImageStates(previous => ({ ...previous, [img.id]: nextSaved }));
      if (saveOnly) {
        onSetsChange(sets.map(set => set.id === (img.setId || img.variationSetId)
          ? { ...set, images: set.images.map(image => image.id === img.id ? { ...image, isSaved: nextSaved } : image) }
          : set));
      }
      toast({ title: nextSaved ? "Saved to My Ads" : "Removed from My Ads" });
      return true;
    } catch (e: any) {
      toast({ title: nextSaved ? "Couldn't save to My Ads" : "Couldn't remove from My Ads", description: e?.message || "Unknown error", variant: "destructive" });
      return false;
    } finally {
      setSavingImageId(null);
    }
  };

  return <div className="space-y-6">
    {sets.map(set => {
      const images = [...set.images].sort((a, b) => a.variantNumber - b.variantNumber);
      const videoSet = set.mediaType === "video" || images.some(i => i.mediaType === "video" || !!i.videoUrl);
       const sizesAvailable = sizeDownloadsEnabled && !videoSet && (!set.mediaType || set.mediaType === "image");
      const resultUrl = (item: VariationResultImage) => videoSet ? item.videoUrl : item.imageUrl;
      const isReady = (item: VariationResultImage) =>
        (item.status === "rendered" || item.status === "complete") && !!resultUrl(item);
      const ready = images.filter(isReady);
      const selected = selections[set.id] ?? new Set<string>();
      const selectedCount = ready.filter(i => selected.has(i.id)).length;
      const all = ready.length > 0 && selectedCount === ready.length;
      const savedCount = ready.filter(i => savedImageStates[i.id] ?? i.isSaved === true).length;
      const failed = set.status === "failed", partial = set.status === "partial", complete = set.status === "complete";
      return <div key={set.id} className="rounded-xl border border-border/60 bg-card p-4 space-y-3" data-testid={`set-${set.id}`}>
        {set.sourceName && <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`set-source-${set.id}`}>
          {set.sourceVideoUrl ? <video src={set.sourceVideoPreviewUrl || set.sourceVideoUrl} aria-label={`${set.sourceName} source video`} className="h-12 w-12 rounded border bg-black object-contain" muted controls playsInline />
            : set.sourceImageUrl && <img src={set.sourceImageUrl} alt="" className="h-9 w-9 rounded object-cover border" />}
          <span>Source: <strong className="text-foreground">{set.sourceName}</strong></span>
          {set.sourceStatus && <Badge variant="outline" className="capitalize">{set.sourceStatus}</Badge>}
        </div>}
        {brandId && set.sourceAdId && <CreativeFamilyInspection brandId={brandId} generatedAdId={set.sourceAdId} label="source ad" />}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            {failed ? <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge> : complete ? <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Complete</Badge> : partial ? <Badge className="bg-amber-500"><AlertCircle className="h-3 w-3 mr-1" />Partial</Badge> : <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />{set.status}</Badge>}
             <span className="text-xs text-muted-foreground" data-testid={`text-set-progress-${set.id}`}>{ready.length} / {videoSet ? 4 : 8} ready</span>
             {saveOnly && ready.length > 0 && <Badge variant="outline"><Save className="h-3 w-3 mr-1" />{savedCount} saved</Badge>}
             {!saveOnly && !!set.keptLockedAt && <Badge variant="outline"><Lock className="h-3 w-3 mr-1" />Saved</Badge>}
          </div>
          <div className="flex gap-2 flex-wrap">
             {!saveOnly && <><span className="text-xs text-muted-foreground self-center" data-testid={`text-set-selected-${set.id}`}>{selectedCount} selected</span>
               <Button variant="ghost" size="sm" className="h-8 text-xs" disabled={!ready.length} onClick={() => change(set.id, () => new Set(all ? [] : ready.map(i => i.id)))} data-testid={`button-toggle-all-${set.id}`}>{all ? "Clear" : "Select all"}</Button>
               <Button variant="outline" size="sm" className="h-8" disabled={!selectedCount || locking === set.id} onClick={() => lock(set)} data-testid={`button-lock-set-${set.id}`}><Lock className="h-3.5 w-3.5 mr-1" />{set.keptLockedAt ? "Update selection" : "Lock in selections"}</Button></>}
              <Button size="sm" className="h-8" disabled={!ready.length || zipping === set.id} onClick={() => zip(set)} data-testid={`button-download-set-${set.id}`}>{zipping === set.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileArchive className="h-3.5 w-3.5 mr-1" />}Download {videoSet ? "video set" : ".zip"} ({saveOnly ? ready.length : set.keptLockedAt ? selectedCount : ready.length})</Button>
              {sizesAvailable && images.some(img => !sizeDownloadExclusion(img)) && <IterationSizesActions setId={set.id} controller={sizeDownloads} />}
          </div>
        </div>
        {set.error && <p className="text-xs text-destructive">{set.error}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {videoSet && set.sourceVideoUrl && <div data-testid={`set-original-video-${set.id}`} className="group overflow-hidden rounded-lg border bg-card">
            <div className="relative bg-muted" style={{ aspectRatio: (set.aspectRatio ?? "4:5").replace(":", " / ") }}>
              <video src={set.sourceVideoPreviewUrl || set.sourceVideoUrl} aria-label="Original source video" className="h-full w-full bg-black object-contain" controls playsInline preload="metadata" />
              <div className="absolute top-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/55 text-white">Original</div>
            </div>
            <div className="flex h-9 items-center gap-1 border-t bg-card px-1.5">
              <span className="min-w-0 flex-1 truncate px-1 text-[10px] font-medium text-muted-foreground">Original source</span>
              <button type="button" aria-label="Download original source video" onClick={() => downloadVideo(set.sourceVideoUrl!, `set-${set.id.slice(0, 6)}-original`)} className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`button-download-original-video-${set.id}`}><Download className="h-3.5 w-3.5" /></button>
            </div>
          </div>}
          {images.map(img => {
           const rendered = isReady(img), chosen = rendered && selected.has(img.id);
           const mediaUrl = resultUrl(img);
           const savedToMyAds = savedImageStates[img.id] ?? img.isSaved === true;
           const hasSizeDownloads = sizesAvailable && !sizeDownloadExclusion(img);
           const saveButton = <button type="button" aria-label={`${savedToMyAds ? "Saved" : "Save"} variation ${img.variantNumber} ${savedToMyAds ? "in" : "to"} My Ads`} disabled={savingImageId === img.id || savedToMyAds} onClick={e => { e.stopPropagation(); void saveToMyAds(img); }} className={`inline-flex min-w-0 flex-1 items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-default ${savedToMyAds ? "text-emerald-700" : ""}`} data-testid={`button-save-image-${img.id}`}>{savingImageId === img.id ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : savedToMyAds ? <Check className="h-3 w-3 shrink-0" /> : <Save className="h-3 w-3 shrink-0" />}<span className="truncate">{savingImageId === img.id ? "Saving…" : savedToMyAds ? "Saved to My Ads" : "Save to My Ads"}</span></button>;
           return <div key={img.id} onClick={() => !saveOnly && rendered && locking !== set.id && change(set.id, s => { chosen ? s.delete(img.id) : s.add(img.id); return s; })} data-testid={`set-image-${img.id}`} className={`group overflow-hidden rounded-lg border bg-card ${!saveOnly && chosen ? "ring-2 ring-violet-500 border-violet-500" : ""} ${!saveOnly && rendered ? "cursor-pointer" : ""}`}>
            <div className="relative bg-muted" style={{ aspectRatio: (set.aspectRatio ?? "4:5").replace(":", " / ") }}>
                {rendered ? <>{videoSet
                  ? <video src={mediaUrl!} aria-label={`Video variation ${img.variantNumber}`} className="h-full w-full bg-black object-contain" controls playsInline preload="metadata" onClick={e => e.stopPropagation()} />
                  : <AdImage src={mediaUrl!} alt={`Variation ${img.variantNumber}`} className="w-full h-full object-cover" loading="lazy" />}
                  {!videoSet && showSafeZone && safeZoneOverlay}{!saveOnly && <div className={`absolute top-1.5 right-1.5 h-6 w-6 rounded-md flex items-center justify-center ${chosen ? "bg-violet-600 text-white" : "bg-black/40 text-transparent"}`} data-testid={`checkbox-keep-${img.id}`}><Check className="h-3.5 w-3.5" /></div>}</> : img.status === "failed" ? <div className="absolute inset-0 grid place-items-center text-destructive"><AlertCircle /></div> : <div className="absolute inset-0 grid place-items-center"><Loader2 className="animate-spin" /></div>}
              <div className="absolute top-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/55 text-white">{img.isOriginal ? "Original" : `#${img.variantNumber}`}</div>
            </div>
            {rendered && <div className={`flex ${hasSizeDownloads ? "min-h-9 flex-wrap py-1" : "h-9"} items-center gap-1 border-t bg-card px-1.5`}>
               {img.isOriginal ? <span className="min-w-0 flex-1 truncate px-1 text-[10px] font-medium text-muted-foreground">Original source</span> : img.generatedAdId ? hasSizeDownloads
                 ? <IterationSizesActions setId={set.id} resultId={img.id} controller={sizeDownloads} action="save" alreadySaved={savedToMyAds} onSaveCurrent={() => saveToMyAds(img)} fallback={saveButton} />
                 : saveButton : <span className="min-w-0 flex-1 truncate px-1 text-[10px] text-muted-foreground">Preparing My Ads…</span>}
               <button type="button" aria-label={`Download ${videoSet ? "video " : ""}variation ${img.variantNumber}`} onClick={e => { e.stopPropagation(); const downloadUrl = saveOnly ? `/api/creative-tinder/variation-sets/${set.id}/results/${img.id}/download` : mediaUrl!; (videoSet ? downloadVideo : downloadOne)(downloadUrl, `set-${set.id.slice(0, 6)}-${img.variantNumber}`); }} className={`inline-flex h-7 shrink-0 items-center justify-center gap-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground ${saveOnly ? "px-1.5 text-[10px] font-medium" : "w-7"}`} data-testid={`button-download-image-${img.id}`}><Download className="h-3.5 w-3.5" />{saveOnly && <span>Download</span>}</button>
               {hasSizeDownloads && <div className="w-full"><IterationSizesActions setId={set.id} resultId={img.id} controller={sizeDownloads} /></div>}
            </div>}
             {brandId && <div className="px-1.5 pb-1"><CreativeFamilyInspection brandId={brandId} generatedAdId={img.generatedAdId ?? undefined} label={`variation ${img.variantNumber}`} /></div>}
          </div>;
        })}</div>
      </div>;
    })}
  </div>;
}